import { createFileRoute, Link } from '@tanstack/react-router';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Button, Badge, toast } from '@ludolio/shared-ui';
import { Download } from 'lucide-react';
import { useState } from 'react';
import { SdkLinks } from '@/components/SdkLinks';
import { apiClient } from '@/services/api';
import { GAME_PLATFORMS } from '@/types';
import { useAuthStore } from '@/stores/authStore';

export const Route = createFileRoute('/_authenticated/getting-started')({
  component: GettingStartedPage,
});

function GettingStartedPage() {
  const user = useAuthStore((s) => s.user);
  const [downloadingPlatform, setDownloadingPlatform] = useState<'windows-x64' | 'mac-universal' | null>(null);

  const triggerBrowserDownload = (url: string, fileName?: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    if (fileName) link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleDesktopAppDownload = async (platform: 'windows-x64' | 'mac-universal') => {
    const platformLabel = platform === 'windows-x64' ? 'Windows' : 'Mac';

    try {
      setDownloadingPlatform(platform);
      const response = await apiClient.getLatestManualAppDownloadUrl(platform);

      if (!response?.success || !response.data?.downloadUrl) {
        throw new Error(response?.message || `Failed to get latest ${platformLabel} download`);
      }

      triggerBrowserDownload(response.data.downloadUrl, response.data.fileName);
    } catch (error: any) {
      console.error(`Failed to download latest ${platformLabel} app:`, error);
      toast.error(error?.response?.data?.message || error?.message || `Failed to download latest ${platformLabel} app`);
    } finally {
      setDownloadingPlatform(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Getting Started</h1>
          <p className="text-muted-foreground mt-1">
            Integrate with Ludolio, upload builds via the web portal, and test your game.
          </p>
        </div>
        {user?.isVerifiedDeveloper ? (
			<Badge variant="success">Verified Developer</Badge>
        ) : (
          <Badge variant="secondary">Pending Verification</Badge>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Quickstart (end-to-end)</CardTitle>
          <CardDescription>Recommended workflow for first-time submissions.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <ol className="list-decimal pl-6 space-y-2 text-sm">
            <li>
              Create a game entry in <Link to="/games" className="underline">My Games</Link> (requires verification to submit).
            </li>
            <li>
              Upload icon + banner in the game page under <span className="font-medium">Media</span>.
            </li>
            <li>
              Upload your first build under <span className="font-medium">Build</span> (ZIP + version + platform + executable path).
            </li>
            <li>
              (Optional) If you’re using the SDK, configure <span className="font-medium">Achievements</span> and <span className="font-medium">Stats</span>.
            </li>
            <li>
              Test the game via the Ludolio Desktop Client (no separate sandbox environment).
            </li>
          </ol>

          <div className="flex flex-wrap items-center gap-3 pt-2 border-t">
            <span className="text-sm text-muted-foreground">Desktop app:</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDesktopAppDownload('windows-x64')}
              disabled={downloadingPlatform !== null}
            >
              <Download className="w-4 h-4 mr-2" />
              {downloadingPlatform === 'windows-x64' ? 'Preparing...' : 'Windows'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDesktopAppDownload('mac-universal')}
              disabled={downloadingPlatform !== null}
            >
              <Download className="w-4 h-4 mr-2" />
              {downloadingPlatform === 'mac-universal' ? 'Preparing...' : 'Mac'}
            </Button>
          </div>

          <div className="flex flex-wrap gap-3">
            <Link to="/games/add">
              <Button>Create a Game</Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>DRM-free vs SDK-integrated</CardTitle>
          <CardDescription>Choose based on whether you need platform services.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <span className="font-medium">DRM-free</span>: your game launches without session validation.
              Use this if you don’t need Achievements/Stats integration.
            </li>
            <li>
              <span className="font-medium">SDK-integrated</span>: enables platform features (e.g. achievements, stats) and secure sessions.
              Leave <span className="font-medium">DRM-free</span> unchecked.
            </li>
          </ul>
          <p className="text-muted-foreground">
            You can toggle this in your game’s <span className="font-medium">Details</span> tab.
          </p>
          <SdkLinks className="border-t pt-4" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Build packaging & upload requirements</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <ul className="list-disc pl-6 space-y-1">
            <li>Build file must be a <span className="font-medium">.zip</span> archive.</li>
            <li>
              You must provide an <span className="font-medium">Executable Path</span>: the relative path to the executable
              <span className="font-medium"> inside the zip</span>.
            </li>
            <li>Uploads are private; downloads use short-lived signed URLs via the Desktop Client.</li>
          </ul>

          <div className="rounded-md bg-muted p-4">
            <p className="font-medium mb-2">Executable Path examples</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Windows: <code>MyGame.exe</code></li>
              <li>macOS (app bundle): <code>MyGame.app</code> (or the binary inside the bundle)</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Supported platforms</CardTitle>
          <CardDescription>Upload one build per platform (you can upload multiple versions).</CardDescription>
        </CardHeader>
        <CardContent className="text-sm">
          <div className="grid md:grid-cols-2 gap-2">
            {GAME_PLATFORMS.map((p) => (
              <div key={p.value} className="flex items-center justify-between rounded border px-3 py-2">
                <span className="font-medium">{p.label}</span>
                <code className="text-xs text-muted-foreground">{p.value}</code>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Testing (no sandbox)</CardTitle>
          <CardDescription>How to validate your uploaded build before release.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <ul className="list-disc pl-6 space-y-1">
            <li>Do a local smoke test on each target OS before uploading (launch, save/load, controller/keyboard, resolution).</li>
            <li>After upload, test via the Ludolio Desktop Client (your developer account can access your own games, including drafts).</li>
            <li>If your game uses the SDK, verify session/auth flow and any Achievements/Stats you configured in the portal.</li>
          </ul>
          <p className="text-muted-foreground">
            Note: since there is no separate test environment, please treat uploads as real artifacts and increment versions for new builds.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
